import React from 'react';
import PresentationModal from '../../organisms/PresentationModal';

export type RuleInfo = {
    id: string;
    pillar: string;
    name: string;
    description: string;
    severity: string;
    threshold?: string;
};

export const RULES: Record<string, RuleInfo> = {
    'ARCH-001': { id: 'ARCH-001', pillar: 'Arquitetura', name: 'Domain importa infra', description: 'Camada de domínio não pode importar módulos de infraestrutura ou adapters. Viola o princípio de inversão de dependência.', severity: 'critical', threshold: 'Qualquer import de adapters/infra no domain' },
    'ARCH-002': { id: 'ARCH-002', pillar: 'Arquitetura', name: 'Domain importa framework', description: 'Camada de domínio não pode depender de frameworks externos (boto3, sqlalchemy, requests, etc). Domain deve ser puro.', severity: 'critical', threshold: 'Qualquer import de framework no domain' },
    'ARCH-003': { id: 'ARCH-003', pillar: 'Arquitetura', name: 'Application importa entrypoint', description: 'Camada de application não deve importar handlers/entrypoints. Application orquestra, não conhece como é invocada.', severity: 'warning', threshold: 'Import de handlers na application' },
    'ARCH-004': { id: 'ARCH-004', pillar: 'Arquitetura', name: 'Lógica de negócio em adapter', description: 'Regras de negócio devem estar no domain, não em controllers ou adapters.', severity: 'warning', threshold: 'Lógica complexa fora do domain' },
    'ARCH-005': { id: 'ARCH-005', pillar: 'Arquitetura', name: 'Ciclo entre camadas', description: 'Dependência circular entre camadas (ex: domain → adapters → domain). Quebra a hierarquia de dependências.', severity: 'critical', threshold: 'Qualquer ciclo detectado' },
    'CODE-001': { id: 'CODE-001', pillar: 'Complexidade', name: 'CC moderada', description: 'Complexidade ciclomática entre 10 e 15. Função difícil de testar e manter. Considere extrair responsabilidades.', severity: 'warning', threshold: 'CC entre 10 e 15' },
    'CODE-002': { id: 'CODE-002', pillar: 'Complexidade', name: 'CC alta', description: 'Complexidade ciclomática acima de 15. Função muito complexa, alto risco de bugs. Refatoração urgente.', severity: 'critical', threshold: 'CC > 15' },
    'CODE-003': { id: 'CODE-003', pillar: 'Complexidade', name: 'Função longa', description: 'Função com muitas linhas. Dificulta leitura, teste e manutenção.', severity: 'warning', threshold: 'Warning: >40 linhas, Critical: >80 linhas' },
    'CODE-004': { id: 'CODE-004', pillar: 'Complexidade', name: 'Arquivo longo', description: 'Arquivo com muitas linhas. Pode indicar múltiplas responsabilidades no mesmo módulo.', severity: 'warning', threshold: 'Warning: >300 linhas, Critical: >500 linhas' },
    'CODE-005': { id: 'CODE-005', pillar: 'Complexidade', name: 'Muitos parâmetros', description: 'Função com muitos parâmetros. Considere usar objetos de valor ou dataclasses.', severity: 'warning', threshold: 'Warning: >5, Critical: >8 parâmetros' },
    'TEST-001': { id: 'TEST-001', pillar: 'Testes', name: 'Módulo sem teste', description: 'Módulo de código fonte sem arquivo de teste correspondente. Reduz confiança em mudanças.', severity: 'warning', threshold: 'Sem arquivo test_*.py correspondente' },
    'OPS-001': { id: 'OPS-001', pillar: 'Operabilidade', name: 'Except genérico', description: 'Uso de except Exception ou except sem tipo específico. Engole erros e dificulta diagnóstico em produção.', severity: 'warning', threshold: 'except Exception / bare except' },
    'OPS-002': { id: 'OPS-002', pillar: 'Operabilidade', name: 'Entrypoint sem logging', description: 'Lambda handler ou entrypoint sem import de logger. Dificulta troubleshooting em produção.', severity: 'warning', threshold: 'Handler sem import de logging/Logger' },
    'SEC-001': { id: 'SEC-001', pillar: 'Segurança', name: 'Credencial hardcoded', description: 'Possível credencial, token ou senha hardcoded no código. Risco de vazamento de segredos.', severity: 'critical', threshold: 'Padrão de secret detectado no código' },
};

type Props = {
    ruleId: string | null;
    show: boolean;
    onClose: () => void;
};

const severityBadge: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
};

const RuleDetailModal: React.FC<Props> = ({ ruleId, show, onClose }) => {
    const rule = ruleId ? RULES[ruleId] : null;

    if (!rule) return null;

    return (
        <PresentationModal id="rule-detail-modal" show={show} onClose={onClose} header={`${rule.id} — ${rule.name}`}>
            <div className="p-5 space-y-4">
                <div className="flex gap-3">
                    <span className={`border text-xs font-bold px-2 py-1 rounded ${severityBadge[rule.severity] || 'bg-gray-100'}`}>{rule.severity}</span>
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold px-2 py-1 rounded">{rule.pillar}</span>
                </div>
                <div>
                    <p className="text-sm text-gray-500 mb-1">Descrição</p>
                    <p className="text-gray-800">{rule.description}</p>
                </div>
                {rule.threshold && (
                    <div>
                        <p className="text-sm text-gray-500 mb-1">Threshold</p>
                        <p className="font-mono text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">{rule.threshold}</p>
                    </div>
                )}
            </div>
        </PresentationModal>
    );
};

export default RuleDetailModal;
